import { graphql } from '@gql';

export const GET_SECURITY_NOTIFICATION = graphql(`
    query GetSecurityNotification($id: ID!) {
        notifications(where: { id: $id }) {
            edges {
                node {
                    ...NotificationFragment
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_FOR_COMPONENT = graphql(`
    query GetSecurityNotificationForComponent($id: ID!) {
        notifications(where: { hasComponentsWith: { id: $id } }) {
            edges {
                node {
                    ...NotificationFragment
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_FOR_PROJECT_SERVER = graphql(`
    query GetSecurityNotificationForProjectServer(
        $pid: ID!
        $orderBy: NotificationOrder
        $where: NotificationWhereInput
        $first: Int
        $before: Cursor
        $after: Cursor
    ) {
        projects(where: { id: $pid }) {
            edges {
                node {
                    id
                    name
                    __typename
                    notifications(
                        before: $before
                        after: $after
                        where: $where
                        orderBy: $orderBy
                        first: $first
                        resolved: false
                    ) {
                        totalCount
                        edges {
                            node {
                                id
                                title
                                publishDate
                                lastUpdateDate
                                impact
                                source
                                cves {
                                    id
                                    code
                                }
                                cvssVector
                                cvss {
                                    score
                                }
                                customizedCVSS {
                                    score
                                }
                                affectedAssetItemsCount
                            }
                        }
                    }
                }
            }
        }
    }
`);

// Every hideable table column is gated behind its own @include so a hidden
// column stops fetching its backing field(s) (id/title are the only columns
// that can't be hidden, so they stay unconditional). Info cards fetch their
// own fields by ID via GET_SECURITY_NOTIFICATIONS_SERVER_DETAILS, so this
// query only needs to carry what the grid itself renders.
export const GET_SECURITY_NOTIFICATIONS_SERVER = graphql(`
    query GetSecurityNotificationsServer(
        $orderBy: NotificationOrder
        $where: NotificationWhereInput
        $first: Int
        $before: Cursor
        $after: Cursor
        $last: Int
        $search: String
        $showCvss: Boolean! = true
        $showCustomizedCvss: Boolean! = true
        $showCvssVector: Boolean! = false
        $showSource: Boolean! = false
        $showCves: Boolean! = true
        $showPublishDate: Boolean! = true
        $showLastUpdateDate: Boolean! = true
        $showImpact: Boolean! = true
    ) {
        notifications(
            before: $before
            after: $after
            last: $last
            where: $where
            orderBy: $orderBy
            first: $first
            search: $search
        ) {
            totalCount
            pageInfo {
                startCursor
                endCursor
                hasNextPage
                hasPreviousPage
            }
            edges {
                cursor
                node {
                    id
                    title
                    publishDate @include(if: $showPublishDate)
                    lastUpdateDate @include(if: $showLastUpdateDate)
                    impact @include(if: $showImpact)
                    source @include(if: $showSource)
                    cves @include(if: $showCves) {
                        code
                    }
                    cvssVector @include(if: $showCvssVector)
                    # Backend Notification.cvss resolver (ent field collection)
                    # computes the score from CvssVector; it returns 0 unless
                    # cvssVector is also selected, so also request it here
                    # whenever the CVSS score column is visible (GraphQL
                    # merges this with the cvssVector selection above).
                    cvssVector @include(if: $showCvss)
                    cvss @include(if: $showCvss) {
                        score
                    }
                    # Same dependency as above but for Notification.customizedCVSS,
                    # which derives its score from cvssOverrideVector.
                    cvssOverrideVector @include(if: $showCustomizedCvss)
                    customizedCVSS @include(if: $showCustomizedCvss) {
                        score
                    }
                }
            }
        }
    }
`);

export const GET_SECURITY_NOTIFICATIONS_SERVER_DETAILS = graphql(`
    query GetSecurityNotificationsServerDetails($notificationID: ID!) {
        notifications(where: { id: $notificationID }) {
            edges {
                node {
                    id
                    title
                    publishDate
                    lastUpdateDate
                    impact
                    source
                    cves {
                        id
                        code
                        description
                        weakness
                        publishDate
                        lastUpdateDate
                        cvssVector
                        cvssBaseScore
                        source
                    }
                    cvssVector
                    cvssOverrideVector
                    solutionStatus
                    action
                    solutionDetails
                    references
                    legalNotice
                    vendorAdvisories {
                        name
                    }
                    components {
                        id
                        name
                        cpeName
                        version
                        eolReached
                        eolDate
                        monitoredSince
                        componentVendor {
                            id
                            name
                            ifa
                            description
                        }
                    }
                }
            }
        }
    }
`);

export const GET_COMPONENT_NOTIFICATIONS_FOR_PROJECT = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query getProjectComponentsNotifications(
        $pid: ID!
        $where: ProjectComponentNotificationWhereInput
        $orderBy: ProjectComponentNotificationOrder
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: $where
            orderBy: $orderBy
            first: 100
        ) {
            edges {
                node {
                    id
                    notificationID
                    notificationTitle
                    notificationPriority
                    notificationPublishDate
                    notificationLastUpdateDate
                    notificationCvssBaseScore
                    notificationCvssTemporalScore
                    notificationCvssOveralScore
                    notificationCvssVector
                    notificationImpact
                    notificationSource
                    componentID
                    componentName
                    componentVersion
                    componentIsValid
                    componentEolReached
                    componentCpeName
                    componentVendorID
                    componentVendorName
                    assetItemID
                    assetItemName
                    assetID
                    assetName
                    assetAlias
                    assetStatus
                    assetRating
                    assetTypeID
                    assetTypeName
                    projectZoneID
                    projectZoneName
                    projectZoneExposureRating
                    projectID
                    mitigationID
                    mitigationStatus
                    mitigationAssignee
                    measureID
                    measureName
                    measureType
                    mitigationTestID
                    mitigationTestResult
                    customizedCVSS {
                        score
                        vector
                    }
                }
            }
        }
    }
`);

export const GET_COMPONENT_NOTIFICATIONS_FOR_PROJECT_SERVER = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query getProjectComponentsNotificationsServer(
        $pid: ID!
        $first: Int
        $where: ProjectComponentNotificationWhereInput
        $orderBy: ProjectComponentNotificationOrder
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: $where
            orderBy: $orderBy
            first: $first
        ) {
            edges {
                node {
                    id
                    notificationID
                    notificationTitle
                    notificationPriority
                    notificationPublishDate
                    notificationLastUpdateDate
                    notificationCvssBaseScore
                    notificationCvssTemporalScore
                    notificationCvssOveralScore
                    notificationCvssVector
                    notificationImpact
                    notificationSource
                    componentID
                    componentName
                    componentVersion
                    componentIsValid
                    componentEolReached
                    componentCpeName
                    componentVendorID
                    componentVendorName
                    assetItemID
                    assetItemName
                    assetID
                    assetName
                    assetAlias
                    assetStatus
                    assetRating
                    assetTypeID
                    assetTypeName
                    projectZoneID
                    projectZoneName
                    projectZoneExposureRating
                    projectID
                    mitigationID
                    mitigationStatus
                    mitigationAssignee
                    measureID
                    measureName
                    measureType
                    mitigationTestID
                    mitigationTestResult
                    customizedCVSS {
                        score
                        vector
                    }
                }
            }
            totalCount
        }
    }
`);

export const GET_UNRESOLVED_NOTIFICATIONS_BY_ZONE = graphql(`
    extend type ProjectComponentNotification
        @typePolicy(keyFields: "assetItemID notificationID componentID")
    query GetUnresolvedNotificationsByZone(
        $pid: ID!
        $zoneID: ID!
        $first: Int
    ) {
        projectComponentsNotifications(
            projectID: $pid
            where: {
                projectZoneID: $zoneID
                and: [
                    {
                        or: [
                            { mitigationIDIsNil: true }
                            {
                                mitigationIDIsNil: false
                                mitigationStatusNotIn: ["COMPLETED", "OBSOLETE"]
                            }
                        ]
                    }
                    { assetStatusNotIn: ["INVALID"] }
                ]
            }
            first: $first
        ) {
            edges {
                node {
                    assetItemID
                    componentID
                    projectZoneID
                    notificationID
                    mitigationStatus
                }
            }
            totalCount
        }
    }
`);

// Default value for first parameter
export const UNRESOLVED_NOTIFICATIONS_FIRST_DEFAULT = 100;

export const GET_MEASURES_FOR_PROJECT = graphql(`
    query getMeasuresForProject($pid: ID!) {
        measures(where: { projectID: $pid }) {
            edges {
                node {
                    id
                    createTime
                    updateTime
                    name
                    type
                    howto
                    resources
                    justification
                    updateToComponent {
                        id
                        name
                        componentVendor {
                            id
                            name
                        }
                        version
                    }
                    mitigations {
                        id
                    }
                }
            }
            totalCount
        }
    }
`);

export const GET_MITIGATIONS = graphql(`
    query getMitigations($input: MitigationWhereInput!) {
        mitigations(where: $input) {
            edges {
                node {
                    ...MitigationFragment
                }
            }
            totalCount
        }
    }
`);

export const GET_MITIGATIONS_SERVER = graphql(`
    query getMitigationsServer(
        $where: MitigationWhereInput!
        $first: Int
        $orderBy: MitigationOrder
    ) {
        mitigations(where: $where, first: $first, orderBy: $orderBy) {
            edges {
                node {
                    ...MitigationFragment
                }
            }
            totalCount
        }
    }
`);

export const GET_MEASURES_SERVER = graphql(`
    query getMeasuresServer(
        $where: MeasureWhereInput!
        $first: Int
        $orderBy: MeasureOrder
    ) {
        measures(where: $where, first: $first, orderBy: $orderBy) {
            edges {
                node {
                    id
                    createTime
                    updateTime
                    name
                    type
                    howto
                    resources
                    justification
                    updateToComponent {
                        id
                        name
                        componentVendor {
                            id
                            name
                        }
                        version
                    }
                    mitigations {
                        id
                    }
                }
            }
            totalCount
        }
    }
`);
